import { redirect, type ParsedLocation } from '@tanstack/react-router';
import { toast } from '@eureca/ui';
import { DEFAULT_COMPANIES_SEARCH } from '@/features/companies/companies-list/companies-search.schema';

/**
 * Workspace guard (App Consultor — "área de trabalho").
 *
 * The internal pages that operate on a single company (Programas, Vagas, Forms)
 * are always reached by entering a company from the Home. The company rides in
 * the URL as `?companyId=<uuid>` — the URL stays the single source of truth
 * (shareable, bookmarkable). Accessing these pages without one — a direct
 * sidebar click or a hand-typed URL with no company ever selected — bounces the
 * consultant back to the Home with a toast (EU-374: "the listing is always
 * preceded by selecting a company on the Home").
 *
 * Resilience fallback: many in-app flows (the job-/program-edit wizards)
 * navigate back to the list without re-passing `companyId`. Rather than thread
 * the id through every one of those ~20 screens, we remember the last entered
 * company for the session and, when a navigation drops it, RE-ATTACH it to the
 * same destination instead of bouncing to the Home. This keeps the URL
 * authoritative while surviving context loss. Only a genuinely cold access
 * (no company ever entered this session) falls through to the Home.
 */
const LAST_WORKSPACE_KEY = 'recruiter:last-company-id';

function rememberWorkspace(companyId: string): void {
  try {
    sessionStorage.setItem(LAST_WORKSPACE_KEY, companyId);
  } catch {
    // Private mode / storage disabled — the URL still works, we just lose the
    // cross-navigation fallback. Non-fatal.
  }
}

function recallWorkspace(): string | undefined {
  try {
    return sessionStorage.getItem(LAST_WORKSPACE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * The company/área de trabalho the consultant is currently operating in (the
 * last one entered this session). Read by the Home to badge the selected card.
 * `undefined` when none was ever entered.
 */
export function getSelectedWorkspaceId(): string | undefined {
  return recallWorkspace();
}

export function requireWorkspace({
  search,
  location,
  cause,
}: {
  search: { companyId?: string };
  location: ParsedLocation;
  cause?: string;
}): void {
  if (search.companyId) {
    rememberWorkspace(search.companyId);
    return;
  }

  // In-app navigation dropped the workspace but the consultant has one this
  // session — re-attach it to the same page (no Home bounce, no toast).
  const remembered = recallWorkspace();
  if (remembered) {
    throw redirect({
      to: location.pathname,
      search: { ...(location.search as Record<string, unknown>), companyId: remembered },
      replace: true,
    });
  }

  // No company ever entered — genuine "no workspace selected" (direct sidebar
  // click / cold deep link). Don't toast on hover-preload (`intent`).
  if (cause !== 'preload') {
    toast.error('Selecione uma área de trabalho para continuar');
  }
  throw redirect({ to: '/', search: DEFAULT_COMPANIES_SEARCH });
}
